import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createReference, resolveId } from "@medplum/core";
import { Observation } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { notifications } from "@mantine/notifications";

import * as Sentry from "@sentry/react";

import { useTimerContext } from "@/components/Timer/TimerContext";
import {
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_QUICK_ADD_MESSAGE,
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
  LABOR_AMEND_QUEUED_MESSAGE,
  LABOR_AMEND_QUICK_ADD_SUCCESS_MESSAGE,
  LABOR_AMEND_QUICK_ADD_SUCCESS_TITLE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { QuestionnaireType } from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import {
  NEWBORN_SUBJECT_QUESTIONNAIRE_TYPES,
  PostpartumClientQuestionnaireType,
  PostpartumNewbornQuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { LaborQuestionnaireDrawer } from "@/encounters/components/EncounterLabor/EncounterLaborDrawer/LaborQuestionnaireDrawer";
import { NewbornFlowsheetSubjectDrawer } from "@/encounters/components/EncounterLabor/EncounterLaborDrawer/NewbornFlowsheetSubjectDrawer";
import { useEnqueueLaborAmendPersist } from "@/encounters/components/EncounterLabor/hooks/use-enqueue-labor-amend-persist";
import { useResolveLaborAmendEncounterParentVersionIdForStamp } from "@/encounters/components/EncounterLabor/hooks/use-labor-amend-encounter-parent-version-id";
import { useLaborQuestionnaireForm } from "@/encounters/components/EncounterLabor/hooks/use-labor-questionnaire-form";
import { useNewbornFlowsheetSubject } from "@/encounters/components/EncounterLabor/hooks/useNewbornFlowsheetSubject";
import {
  completeAllEncounterTasks,
  createLaborFlowsheetQuestionnaireResponse,
  fetchLaborStageObservations,
  getOrCreateLaborStage,
} from "@/encounters/components/EncounterLabor/services/labor";
import { applyLaborQuickAddBirthCallbacks } from "@/encounters/components/EncounterLabor/utils/apply-labor-quick-add-birth-callbacks";
import { extractDateTimeFromFormValues } from "@/encounters/components/EncounterLabor/utils/form-datetime";
import { formatNormalizedFormValuesForAmendDisplay } from "@/encounters/components/EncounterLabor/utils/labor-amend-preview";
import { isLaborBirthForm } from "@/encounters/components/EncounterLabor/utils/labor-questionnaire-classification";
import { buildLaborQuestionnaireSubmitValues } from "@/encounters/components/EncounterLabor/utils/labor-questionnaire-submit-values";
import {
  dedupeFinalLaborStageObservations,
  getLaborStageAnswerCode,
} from "@/encounters/components/EncounterLabor/utils/labor-stage-dedupe";
import { resolveLiveLaborStageLoincCodeAtSubmit } from "@/encounters/components/EncounterLabor/utils/resolve-live-labor-stage-loinc-code-at-submit";
import { useBirthMilestonesContext } from "@/encounters/contexts/BirthMilestonesProvider";
import { useLaborAmendFlowsheetPersist } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import { LOINCAnswerCodes } from "@/fhir/enums/loinc";
import { RootState } from "@/store";
import { selectBirthDateTime } from "@/store/slices/birth";
import { loadLaborStages, selectCurrentLaborStage } from "@/store/slices/labor-stages";
import { formatBirthDateTimeForDisplay, isDateTimeOnOrAfterBirth } from "@/utils/datetime";
import { toast } from "@/utils/notifications";

type AllQuestionnaireTypes =
  | QuestionnaireType
  | PostpartumClientQuestionnaireType
  | PostpartumNewbornQuestionnaireType;

// List of postpartum questionnaire types that require birth date validation
const POSTPARTUM_QUESTIONNAIRE_TYPES = new Set<AllQuestionnaireTypes>([
  ...Object.values(PostpartumClientQuestionnaireType),
  ...Object.values(PostpartumNewbornQuestionnaireType),
]);

type LaborFlowsheetQuickAddContext = "labor" | "postpartum";

interface EncounterLaborQuickButtonDrawerProps {
  opened: boolean;
  onClose: () => void;
  questionnaireName: AllQuestionnaireTypes;
  questionnaireTitle: string;
  encounterId: string;
  clientId: string;
  /** Distinguishes labor flowsheet quick-adds from client/newborn postpartum (shared codes like `birthing`). */
  flowsheetContext?: LaborFlowsheetQuickAddContext;
  onRefresh?: () => Promise<void>;
  onBirthCreate?: (birthDateTime: string) => void;
  /** Called when Body Delivered is cleared after a prior optimistic birth update (keeps Redux in sync with the form). */
  onClearOptimisticBirthDateTime?: () => void;
}

export function EncounterLaborQuickButtonDrawer({
  opened,
  onClose,
  questionnaireName,
  questionnaireTitle,
  encounterId,
  clientId,
  flowsheetContext = "labor",
  onRefresh,
  onBirthCreate,
  onClearOptimisticBirthDateTime,
}: EncounterLaborQuickButtonDrawerProps) {
  const currentLaborStage = useSelector<RootState, Observation | undefined>(
    selectCurrentLaborStage,
  );
  const birthDateTime = useSelector(selectBirthDateTime);
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const practitioner = profile?.resourceType === "Practitioner" ? profile : null;
  const [formKey, setFormKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [drawerLaborStageLoincCode, setDrawerLaborStageLoincCode] = useState<string | undefined>();
  const isLaborFlowsheetEntry = flowsheetContext === "labor";
  // The labor flowsheet opens Birthing and Shoulder Dystocia under the same names as the newborn
  // section, so only a postpartum entry from the explicit newborn list is charted on the newborn.
  const isNewbornDomainQuestionnaire =
    flowsheetContext === "postpartum" && NEWBORN_SUBJECT_QUESTIONNAIRE_TYPES.has(questionnaireName);
  // Resolves which newborn this entry belongs to when charting a newborn-domain flowsheet item
  // (Newborn Vitals, Feeding, etc.) — see the hook's own doc comment for the flag-gating and
  // ambiguous/none handling this drives in the render logic below. Gated on `opened`, not just the
  // questionnaire type: this component stays mounted across drawer close/reopen (its callers keep
  // the "which flow was picked" state around), so without this, a twin picked to resolve an
  // ambiguous delivery would silently stay selected for the next, unrelated newborn-domain entry.
  const newbornSubject = useNewbornFlowsheetSubject({
    enabled: isNewbornDomainQuestionnaire && opened,
    motherId: clientId,
    laborEncounterId: encounterId,
  });
  // Shared by the submit guard and the render branch below so the two always agree.
  const newbornSubjectBlocksEntry =
    isNewbornDomainQuestionnaire &&
    newbornSubject.status !== "ready" &&
    newbornSubject.status !== "not-applicable";
  const dispatch = useDispatch();
  // Get timer context
  const { resetTimer, deleteAllTimers, hideAllTimerDisplays, stopTimer } = useTimerContext();
  const { setupHeadDeliveredMilestone, setupBodyDeliveredMilestone, setupRomMilestone } =
    useBirthMilestonesContext();
  const enqueueAmendPersist = useEnqueueLaborAmendPersist();
  const {
    deferFlowsheetPersists,
    registerPostAmendmentEffect,
    registerAmendProvenanceTarget,
    getPendingEntryLaborStageLoincCode,
    getLaborStageObservationsForFlush,
  } = useLaborAmendFlowsheetPersist();
  const resolveEncounterQuestionnaireParentVersionIdForStamp =
    useResolveLaborAmendEncounterParentVersionIdForStamp();
  const showLaborStageInDrawer = deferFlowsheetPersists && isLaborFlowsheetEntry;

  const resolveInitialQuickAddLaborStageLoincCode = useCallback((): string | undefined => {
    if (!isLaborFlowsheetEntry) {
      return undefined;
    }
    if (isLaborBirthForm(questionnaireName)) {
      return LOINCAnswerCodes.SecondStageLabor;
    }
    return (
      (currentLaborStage ? getLaborStageAnswerCode(currentLaborStage) : undefined) ??
      LOINCAnswerCodes.ProdromalLabor
    );
  }, [currentLaborStage, isLaborFlowsheetEntry, questionnaireName]);

  const [baselineRefreshKey, setBaselineRefreshKey] = useState(0);

  const [previousOpened, setPreviousOpened] = useState(opened);
  if (opened !== previousOpened) {
    setPreviousOpened(opened);
    if (opened) {
      setFormKey((previousFormKey) => previousFormKey + 1);
      setBaselineRefreshKey(0);
    }
  }

  const drawerLaborStageSyncKey = `${opened}:${showLaborStageInDrawer}:${formKey}`;
  const [previousDrawerLaborStageSyncKey, setPreviousDrawerLaborStageSyncKey] =
    useState(drawerLaborStageSyncKey);
  if (drawerLaborStageSyncKey !== previousDrawerLaborStageSyncKey) {
    setPreviousDrawerLaborStageSyncKey(drawerLaborStageSyncKey);
    if (opened && showLaborStageInDrawer) {
      setDrawerLaborStageLoincCode(resolveInitialQuickAddLaborStageLoincCode());
    } else {
      setDrawerLaborStageLoincCode(undefined);
    }
  }

  const { isLoading, questionnaire, questionnaireResponse, formSchema, form, questionnaireError } =
    useLaborQuestionnaireForm({
      key: formKey,
      questionnaireName,
      encounterId,
      questionnaireId: undefined,
      questionnaireResponse: undefined,
      editMode: false,
    });

  const birthingBodyDateTime = form.values.birthingBodyDateTime as string | undefined;
  const shoulderDystociaBirth = form.values.shoulderDystociaBirth as string | undefined;

  // When opening the Birthing form, prefill Body Delivered with the current birthDateTime
  // (and whenever birthDateTime changes while open) so edits from other forms
  // like Shoulder Dystocia are reflected. Gated on `!isLoading` so this runs after
  // useLaborQuestionnaireForm's own fetch-then-reset has settled, not before it - otherwise the
  // reset silently overwrites this on a slow (uncached) first fetch.
  useEffect(() => {
    if (opened && questionnaireName === QuestionnaireType.Birthing && !isLoading && birthDateTime) {
      form.setFieldValue("birthingBodyDateTime", birthDateTime);
    }
    // `form` is stable from `useLaborQuestionnaireForm`; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, questionnaireName, isLoading, birthDateTime]);

  // Whenever Birthing Body Delivered changes while the drawer is open, update the optimistic
  // birth time in Redux, but avoid re-dispatching the same value on every render.
  const lastBirthingBodyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!opened) {
      lastBirthingBodyRef.current = undefined;
    }
  }, [opened]);

  useEffect(() => {
    lastBirthingBodyRef.current = undefined;
  }, [formKey]);

  useEffect(() => {
    if (!opened || questionnaireName !== QuestionnaireType.Birthing || !onBirthCreate) {
      return;
    }

    if (isLoading) {
      return;
    }

    const trimmedBody = typeof birthingBodyDateTime === "string" ? birthingBodyDateTime.trim() : "";

    if (!trimmedBody) {
      if (lastBirthingBodyRef.current !== undefined) {
        lastBirthingBodyRef.current = undefined;
        onClearOptimisticBirthDateTime?.();
      }
      return;
    }

    if (lastBirthingBodyRef.current === trimmedBody) {
      return;
    }

    lastBirthingBodyRef.current = trimmedBody;
    onBirthCreate(trimmedBody);
    // `form` is stable from `useLaborQuestionnaireForm`; safe to omit from deps.
  }, [
    opened,
    questionnaireName,
    birthingBodyDateTime,
    isLoading,
    onBirthCreate,
    onClearOptimisticBirthDateTime,
  ]);

  // Gated on `!isLoading` for the same reason as the Birthing prefill above: without it, a slow
  // (uncached) first fetch's reset-to-defaults races this and silently wins.
  useEffect(() => {
    if (
      opened &&
      questionnaireName === QuestionnaireType.ShoulderDystocia &&
      !isLoading &&
      birthDateTime &&
      !shoulderDystociaBirth
    ) {
      form.setFieldValue("shoulderDystociaBirth", birthDateTime);
    }
    // `form` is stable from `useLaborQuestionnaireForm`; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, questionnaireName, isLoading, birthDateTime, shoulderDystociaBirth]);

  const refreshWithRetry = async () => {
    try {
      if (onRefresh) {
        await onRefresh();
      }
      const observations = await fetchLaborStageObservations(medplum, clientId, encounterId);

      dispatch(loadLaborStages(dedupeFinalLaborStageObservations(observations)));
    } catch (error) {
      console.error("❌ Error in refreshWithRetry:", error);
    }
  };

  const triggerBirthRefresh = () => {
    if (!onRefresh) {
      return;
    }

    setTimeout(() => {
      if (onRefresh) {
        onRefresh();
      }
    }, 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) {
      return;
    }

    if (!questionnaire?.id || !formSchema || !encounterId || !clientId || !practitioner) {
      const missingFields: string[] = [];
      if (!questionnaire?.id) missingFields.push("questionnaire.id");
      if (!formSchema) missingFields.push("formSchema");
      if (!encounterId) missingFields.push("encounterId");
      if (!clientId) missingFields.push("clientId");
      if (!practitioner) missingFields.push("practitioner");
      console.error("handleSubmit precondition failed. Missing fields:", missingFields.join(", "));
      onClose();
      return;
    }

    // A newborn-domain entry with no resolved newborn subject never reaches here through the UI
    // (the render below shows a blocking message/picker instead of this form in that case) — this
    // guard only protects against a submit that somehow fires anyway.
    if (newbornSubjectBlocksEntry) {
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedValues = buildLaborQuestionnaireSubmitValues({
        form,
        formSchema,
        questionnaire,
      });
      if (!normalizedValues) {
        return;
      }

      if (
        POSTPARTUM_QUESTIONNAIRE_TYPES.has(questionnaireName) &&
        !isLaborBirthForm(questionnaireName) &&
        birthDateTime
      ) {
        const { date, time } = extractDateTimeFromFormValues(normalizedValues, questionnaireName);
        // Only validate if both date and time are present
        if (date && time && !isDateTimeOnOrAfterBirth(date, time, birthDateTime)) {
          const birthDisplayTime = formatBirthDateTimeForDisplay(birthDateTime);
          notifications.show({
            title: "Invalid Date/Time",
            message: `The date and time must be on or after the birth time (${birthDisplayTime}). Postpartum entries cannot be recorded before birth.`,
            color: "red",
            autoClose: 8000,
          });
          Sentry.captureException(
            new Error(
              `Invalid date/time: birth time (${birthDisplayTime}), form date time (${date} ${time}). Postpartum entries cannot be recorded before birth.`,
            ),
          );
          console.warn(
            `Invalid date/time: birth time (${birthDisplayTime}), form date time (${date} ${time}). Postpartum entries cannot be recorded before birth.`,
          );
          return;
        }
      }

      const laborStageAtSubmit = currentLaborStage;

      const resolveLaborStageObservation = async (
        laborStageLoincCode: string | undefined,
      ): Promise<Observation | undefined> => {
        const stageCode = laborStageLoincCode ?? LOINCAnswerCodes.ProdromalLabor;
        try {
          const existingStages = await getLaborStageObservationsForFlush(
            medplum,
            clientId,
            encounterId,
            fetchLaborStageObservations,
          );
          const existingStage = existingStages.find(
            (stage) => getLaborStageAnswerCode(stage) === stageCode,
          );
          if (existingStage) {
            return existingStage;
          }
          return await getOrCreateLaborStage(
            medplum,
            existingStages,
            stageCode,
            clientId,
            encounterId,
          );
        } catch (error) {
          throw error instanceof Error
            ? error
            : new Error("Failed to resolve labor stage for this flowsheet entry.");
        }
      };

      const persistFlowsheetQuestionnaire = async (laborStageLoincCodeForPersist?: string) => {
        const stageToUse = isLaborFlowsheetEntry
          ? await resolveLaborStageObservation(
              laborStageLoincCodeForPersist ??
                (laborStageAtSubmit ? getLaborStageAnswerCode(laborStageAtSubmit) : undefined),
            )
          : undefined;

        const savedQuestionnaireResponse = await createLaborFlowsheetQuestionnaireResponse(
          medplum,
          clientId,
          encounterId,
          practitioner,
          questionnaireName,
          normalizedValues,
          stageToUse ? createReference(stageToUse) : undefined,
          onRefresh,
          undefined,
          resolveEncounterQuestionnaireParentVersionIdForStamp(),
          resolveId(newbornSubject.newbornPatientReference),
        );

        if (savedQuestionnaireResponse) {
          registerAmendProvenanceTarget(savedQuestionnaireResponse);
        }
        if (stageToUse) {
          registerAmendProvenanceTarget(stageToUse);
        }

        if (!laborStageAtSubmit) {
          await refreshWithRetry();
        }
      };

      const runLaborQuickAddLocalUiSideEffects = async () => {
        const questionnaireCodes = {
          [QuestionnaireType.FetalHeartRate]: "fhr",
          [QuestionnaireType.CervicalExam]: "cervical",
          [QuestionnaireType.LaborVitals]: "vitals",
          [QuestionnaireType.Contractions]: "contractions",
        } as const;
        const questionnaireCode =
          questionnaireName in questionnaireCodes
            ? questionnaireCodes[questionnaireName as keyof typeof questionnaireCodes]
            : undefined;

        const romDateTimeGroup = normalizedValues.romDateTime as
          | { romDate: string; romTime: string }
          | undefined;
        if (
          questionnaireName === QuestionnaireType.RuptureOfMembranes &&
          romDateTimeGroup?.romDate &&
          romDateTimeGroup?.romTime
        ) {
          setupRomMilestone({
            rom: `${romDateTimeGroup.romDate}T${romDateTimeGroup.romTime}Z`,
          });
          resetTimer("ROM");
        }

        if (
          questionnaireName === QuestionnaireType.Birthing &&
          normalizedValues?.birthingHeadDateTime
        ) {
          stopTimer("ROM");
          setupHeadDeliveredMilestone({
            headDelivered: `${normalizedValues.birthingHeadDateTime}`,
          });
        }

        if (
          questionnaireName === QuestionnaireType.Birthing &&
          normalizedValues?.birthingBodyDateTime
        ) {
          stopTimer("Head Delivered");
          hideAllTimerDisplays();
          deleteAllTimers();
          setupBodyDeliveredMilestone({
            bodyDelivered: `${normalizedValues.birthingBodyDateTime}`,
            updatedAtMs: Date.now(),
          });
        } else if (
          questionnaireName === QuestionnaireType.ShoulderDystocia &&
          normalizedValues?.shoulderDystociaBirth
        ) {
          stopTimer("Head Delivered");
          hideAllTimerDisplays();
          deleteAllTimers();
          setupBodyDeliveredMilestone({
            bodyDelivered: `${normalizedValues.shoulderDystociaBirth}`,
            updatedAtMs: Date.now(),
          });
        } else if (questionnaireCode) {
          resetTimer(questionnaireCode);
        }
      };

      const runLaborQuickAddMedplumSideEffects = async () => {
        if (
          questionnaireName === QuestionnaireType.Birthing &&
          normalizedValues?.birthingBodyDateTime
        ) {
          await completeAllEncounterTasks(medplum, encounterId, clientId);
          return;
        }

        if (
          questionnaireName === QuestionnaireType.ShoulderDystocia &&
          normalizedValues?.shoulderDystociaBirth
        ) {
          await completeAllEncounterTasks(medplum, encounterId, clientId);
        }
      };

      const runLaborQuickAddUiSideEffects = async ({
        includeMedplumWrites,
        includeBirthCallbacks,
        includeSuccessToast,
      }: {
        includeMedplumWrites: boolean;
        includeBirthCallbacks: boolean;
        includeSuccessToast: boolean;
      }) => {
        await runLaborQuickAddLocalUiSideEffects();

        if (includeMedplumWrites) {
          await runLaborQuickAddMedplumSideEffects();
        }

        if (includeBirthCallbacks) {
          applyLaborQuickAddBirthCallbacks({
            questionnaireName,
            normalizedValues: normalizedValues as Record<string, unknown>,
            onBirthCreate,
            triggerBirthRefresh,
          });
        }

        if (includeSuccessToast) {
          toast({
            type: "success",
            title: LABOR_AMEND_QUICK_ADD_SUCCESS_TITLE,
            message: LABOR_AMEND_QUICK_ADD_SUCCESS_MESSAGE,
            autoClose: 3000,
          });
        }
      };

      const runPostPersistSideEffects = async (options?: {
        includeMedplumWrites?: boolean;
        includeBirthCallbacks?: boolean;
        includeSuccessToast?: boolean;
      }) => {
        await runLaborQuickAddUiSideEffects({
          includeMedplumWrites: options?.includeMedplumWrites ?? true,
          includeBirthCallbacks: options?.includeBirthCallbacks ?? true,
          includeSuccessToast: options?.includeSuccessToast ?? true,
        });
      };

      const runPostAmendFlushSideEffects = async () => {
        await runLaborQuickAddLocalUiSideEffects();
        applyLaborQuickAddBirthCallbacks({
          questionnaireName,
          normalizedValues: normalizedValues as Record<string, unknown>,
          onBirthCreate,
          triggerBirthRefresh,
        });
        await runLaborQuickAddMedplumSideEffects();
        toast({
          type: "success",
          title: LABOR_AMEND_QUICK_ADD_SUCCESS_TITLE,
          message: LABOR_AMEND_QUICK_ADD_SUCCESS_MESSAGE,
          autoClose: 3000,
        });
      };

      const runPersistAndEffects = async (laborStageLoincCodeForPersist?: string) => {
        await persistFlowsheetQuestionnaire(laborStageLoincCodeForPersist);
        if (questionnaireName === QuestionnaireType.FetalHeartRate) {
          setBaselineRefreshKey((previousKey) => previousKey + 1);
        }
        if (deferFlowsheetPersists) {
          registerPostAmendmentEffect(runPostAmendFlushSideEffects);
          return;
        }
        await runPostPersistSideEffects();
      };

      if (deferFlowsheetPersists) {
        const pendingKey = `labor-quick-add:${questionnaireName}:${Date.now()}`;
        const selectedLaborStageLoincCode =
          drawerLaborStageLoincCode ?? resolveInitialQuickAddLaborStageLoincCode();

        const wasAmendPersistEnqueued = enqueueAmendPersist({
          pendingDisplayEntry: {
            key: pendingKey,
            entryType: "quick-add",
            title: questionnaireTitle || String(questionnaireName),
            laborStageLoincCode: selectedLaborStageLoincCode,
            details: formatNormalizedFormValuesForAmendDisplay(
              normalizedValues as Record<string, unknown>,
            ),
          },
          persist: async () => {
            const laborStageLoincCodeAtFlush = isLaborFlowsheetEntry
              ? (getPendingEntryLaborStageLoincCode(pendingKey) ?? selectedLaborStageLoincCode)
              : undefined;
            await runPersistAndEffects(laborStageLoincCodeAtFlush);
          },
          queuedMessage: LABOR_AMEND_QUEUED_MESSAGE,
          errorTitle: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
          errorMessage: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_QUICK_ADD_MESSAGE,
          onQueued: () => {
            onClose();
          },
        });
        if (wasAmendPersistEnqueued) {
          return;
        }
      }

      const liveLaborStageLoincCode = resolveLiveLaborStageLoincCodeAtSubmit(
        isLaborFlowsheetEntry,
        laborStageAtSubmit,
      );
      await runPersistAndEffects(liveLaborStageLoincCode);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        type: "error",
        title: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        message:
          error instanceof Error
            ? error.message
            : LABOR_AMEND_FLOWSHEET_SAVE_ERROR_QUICK_ADD_MESSAGE,
        autoClose: false,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!opened) {
    return null;
  }

  // A newborn-domain entry (Newborn Vitals, Feeding, etc.) needs a resolved newborn subject before
  // the actual questionnaire form is worth showing at all -- see NewbornFlowsheetSubjectDrawer's own
  // doc comment for why this renders a lightweight drawer instead of the full LaborQuestionnaireDrawer.
  // "not-applicable" (flag off, or a client-domain questionnaire) falls through unchanged.
  if (newbornSubjectBlocksEntry) {
    return (
      <NewbornFlowsheetSubjectDrawer
        opened={opened}
        onClose={onClose}
        newbornSubject={newbornSubject}
        questionnaireTitle={questionnaireTitle}
      />
    );
  }

  return (
    <LaborQuestionnaireDrawer
      form={form}
      key={formKey}
      opened={opened}
      onClose={onClose}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      error={questionnaireError}
      isSubmitting={isSubmitting}
      questionnaire={questionnaire}
      title={questionnaireTitle || ""}
      submitButtonText="Add To Flowsheet"
      baselineRefreshKey={baselineRefreshKey}
      questionnaireResponse={questionnaireResponse}
      formId={`labor-flowsheet-form-${questionnaireName}`}
      laborStageSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    />
  );
}
